import { useState, useEffect } from "react";
import useDeadlineOverride from "@/hooks/useDeadlineOverride";
import useCourseSettings, { parseDeadlineDate, formatDate } from "@/hooks/useCourseSettings";

type BannerData = {
  icon: string;
  message: string;
  subMessage: string;
  bgClass: string;
  borderClass: string;
  textClass: string;
  iconBgClass: string;
  iconTextClass: string;
  pulse: boolean;
};

function computeBanner(forcedExpired: boolean, deadlineStr: string): BannerData {
  if (forcedExpired) {
    return {
      icon: "ri-timer-line",
      message: "신청이 마감되었습니다",
      subMessage: "정원이 모두 마감되었습니다. 다음 기수를 기다려 주세요.",
      bgClass: "bg-background-200",
      borderClass: "border-background-300",
      textClass: "text-foreground-600",
      iconBgClass: "bg-foreground-400",
      iconTextClass: "text-background-50",
      pulse: false,
    };
  }

  const deadline = parseDeadlineDate(deadlineStr);
  const now = new Date();
  const diffMs = deadline.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  const deadlineDateStr = deadlineStr.includes("T") ? deadlineStr.split("T")[0] : deadlineStr;
  const deadlineFormatted = formatDate(deadlineDateStr);

  if (diffDays < 0) {
    return {
      icon: "ri-timer-line",
      message: "신청이 마감되었습니다",
      subMessage: "기한이 지났습니다. 다음 기수 모집을 기다려 주세요.",
      bgClass: "bg-background-200",
      borderClass: "border-background-300",
      textClass: "text-foreground-600",
      iconBgClass: "bg-foreground-400",
      iconTextClass: "text-background-50",
      pulse: false,
    };
  }

  if (diffDays === 0) {
    return {
      icon: "ri-alarm-warning-line",
      message: "오늘 마감!",
      subMessage: "23:59까지 신청 가능 · 정원 마감 시 조기 종료",
      bgClass: "bg-primary-500",
      borderClass: "border-primary-600",
      textClass: "text-background-50",
      iconBgClass: "bg-background-50",
      iconTextClass: "text-primary-600",
      pulse: true,
    };
  }

  if (diffDays <= 3) {
    return {
      icon: "ri-timer-flash-line",
      message: `곧 마감! D-${diffDays}`,
      subMessage: `${deadlineFormatted} 23:59 · 정원 마감 시 조기 종료`,
      bgClass: "bg-accent-100",
      borderClass: "border-accent-300",
      textClass: "text-accent-800",
      iconBgClass: "bg-accent-500",
      iconTextClass: "text-background-50",
      pulse: true,
    };
  }

  return {
    icon: "ri-timer-flash-line",
    message: `신청 마감: ${deadlineFormatted} 23:59`,
    subMessage: `D-${diffDays} · 정원 마감 시 조기 종료될 수 있습니다`,
    bgClass: "bg-accent-100",
    borderClass: "border-accent-200",
    textClass: "text-accent-800",
    iconBgClass: "bg-accent-500",
    iconTextClass: "text-background-50",
    pulse: false,
  };
}

export default function DeadlineBanner() {
  const { isManuallyClosed } = useDeadlineOverride();
  const { settings } = useCourseSettings();
  const [banner, setBanner] = useState<BannerData>(() =>
    computeBanner(isManuallyClosed, settings.deadline)
  );

  useEffect(() => {
    setBanner(computeBanner(isManuallyClosed, settings.deadline));
  }, [isManuallyClosed, settings.deadline]);

  useEffect(() => {
    const timer = setInterval(() => {
      setBanner(computeBanner(isManuallyClosed, settings.deadline));
    }, 60_000);
    return () => clearInterval(timer);
  }, [isManuallyClosed, settings.deadline]);

  return (
    <div
      className={`flex items-start gap-2.5 px-4 py-3 rounded-lg border ${banner.bgClass} ${banner.borderClass}`}
    >
      <span
        className={`w-5 h-5 flex items-center justify-center rounded-full shrink-0 mt-0.5 ${banner.iconBgClass} ${banner.iconTextClass} ${banner.pulse ? "animate-pulse" : ""}`}
      >
        <i className={`${banner.icon} text-xs`}></i>
      </span>
      <div className={`text-sm ${banner.textClass}`}>
        <span className="font-bold">{banner.message}</span>
        <span className="opacity-80"> · {banner.subMessage}</span>
      </div>
    </div>
  );
}